///
/// EarthBrowser
///
/// Wildfire Data Provider
///
/// Provides wildfire perimeter data from the local sync database with
/// custom rendering for the wildfire browser window
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { DataRow, IDataProvider, DataQuery, QueryResult, ColumnMetadata, DataStats, FilterGroup, ProviderCapabilities } from './types'
import { fireAcreage, isFireActive, type WildfireProperties } from '@/earthbrowser/dataset-distribution/wildfires/WildfiresTable'
import { formatFireArea } from '@/earthbrowser/utils/unitFormatters'
import { formatDistanceToNow } from 'date-fns'

export class WildfireDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'custom' as const
	private queryFn: () => Promise<WildfireProperties[]>
	private cachedData: WildfireProperties[] = []
	private isInitializedFlag = false

	constructor(config: {
		id: string
		name: string
		queryFn: () => Promise<WildfireProperties[]>
	}) {
		this.id = config.id
		this.name = config.name
		this.queryFn = config.queryFn
	}

	async initialize(): Promise<void> {
		if (this.isInitializedFlag) return
		this.cachedData = await this.queryFn()
		this.isInitializedFlag = true
	}

	async refreshData(): Promise<void> {
		this.cachedData = await this.queryFn()
	}

	/**
	 * Update cached data directly (for when data is managed externally)
	 */
	updateCachedData(data: WildfireProperties[]): void {
		this.cachedData = data
	}

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true,
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: false,
		supportsUpdate: false,
		supportsDelete: false,
		supportsTransactions: false,
		supportsIndexing: false,
		supportsAggregation: false,
		supportsJoins: false,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	private columnMetadata: ColumnMetadata[] = [
		{
			id: 'name',
			name: 'Fire',
			priority: 1,
			minWidth: 180,
			type: 'string',
			nullable: false,
			render: (value: unknown, row?: DataRow) => {
				const fire = row as unknown as WildfireProperties | undefined
				const active = fire ? isFireActive(fire) : false
				return (
					<div className="flex items-center gap-2 min-w-0">
						<span
							className={`flex-shrink-0 w-2 h-2 rounded-full ${active ? 'bg-rose-400' : 'bg-slate-500'}`}
							title={active ? 'Active' : 'Contained'}
						/>
						<span className="font-medium truncate">{value as string}</span>
					</div>
				)
			}
		},
		{
			id: 'state',
			name: 'Location',
			priority: 2,
			minWidth: 130,
			type: 'string',
			nullable: true,
			render: (value: unknown, row?: DataRow) => {
				const fire = row as unknown as WildfireProperties | undefined
				const location = [fire?.county, value as string | null].filter(Boolean).join(', ')
				return <span className="text-white/80 truncate">{location || '—'}</span>
			}
		},
		{
			id: 'incident_size',
			name: 'Size',
			priority: 2,
			minWidth: 100,
			type: 'number',
			nullable: true,
			render: (value: unknown, row?: DataRow) => {
				const fire = row as unknown as WildfireProperties | undefined
				const acres = fire ? fireAcreage(fire) : (value as number | null)
				if (acres == null) return <span className="text-white/40">—</span>

				const colorClass = acres >= 100000 ? 'text-red-400' :
									acres >= 10000 ? 'text-orange-400' :
									acres >= 1000 ? 'text-yellow-400' :
									'text-white/80'
				return (
					<span className={`font-semibold ${colorClass}`}>
						{formatFireArea(acres)}
					</span>
				)
			}
		},
		{
			id: 'percent_contained',
			name: 'Contained',
			priority: 3,
			minWidth: 110,
			type: 'number',
			nullable: true,
			render: (value: unknown) => {
				const pct = value as number | null
				if (pct == null) return <span className="text-white/40">—</span>

				const colorClass = pct >= 100 ? 'text-green-400' :
									pct >= 50 ? 'text-yellow-400' :
									'text-orange-400'
				return (
					<div className="flex items-center gap-1.5">
						<div className="w-12 h-1 rounded-full bg-white/10 overflow-hidden">
							<div
								className="h-full rounded-full bg-current opacity-70"
								style={{ width: `${Math.min(100, Math.max(0, pct))}%`, color: 'inherit' }}
							/>
						</div>
						<span className={`text-xs font-medium ${colorClass}`}>{Math.round(pct)}%</span>
					</div>
				)
			}
		},
		{
			id: 'date_current',
			name: 'Updated',
			priority: 4,
			minWidth: 130,
			type: 'date',
			nullable: false,
			render: (value: unknown) => {
				const date = new Date(value as string)
				const exactTime = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
				return (
					<span
						className="text-white/80 cursor-help"
						title={exactTime}
					>
						{formatDistanceToNow(date, { addSuffix: true })}
					</span>
				)
			}
		},
		{
			id: 'fire_cause',
			name: 'Cause',
			priority: 5,
			minWidth: 110,
			type: 'string',
			nullable: true,
			render: (value: unknown) => {
				if (!value) return <span className="text-white/40">—</span>
				return <span className="text-white/70 text-xs">{value as string}</span>
			}
		}
	]

	async getColumns(): Promise<ColumnMetadata[]> {
		return this.columnMetadata
	}

	async getStats(): Promise<DataStats> {
		return {
			totalRows: this.cachedData.length,
			columnCount: this.columnMetadata.length,
			memorySize: JSON.stringify(this.cachedData).length,
			lastModified: new Date()
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		let filteredData = [...this.cachedData]

		// Apply full-text search
		if (query.search?.query) {
			const lowerSearch = query.search.query.toLowerCase()
			filteredData = filteredData.filter((row) => {
				return [row.name, row.state, row.county, row.fire_cause, row.short_description].some((value) => {
					if (value == null) return false
					return String(value).toLowerCase().includes(lowerSearch)
				})
			})
		}

		// Apply sorting
		if (query.sort && query.sort.length > 0) {
			const sortSpec = query.sort[0]
			const sortBy = sortSpec.column
			const sortDirection = sortSpec.direction

			filteredData.sort((a, b) => {
				const aVal = a[sortBy as keyof WildfireProperties]
				const bVal = b[sortBy as keyof WildfireProperties]

				if (aVal == null && bVal == null) return 0
				if (aVal == null) return 1
				if (bVal == null) return -1

				let comparison = 0
				if (typeof aVal === 'string' && typeof bVal === 'string') {
					comparison = aVal.localeCompare(bVal)
				} else if (typeof aVal === 'number' && typeof bVal === 'number') {
					comparison = aVal - bVal
				} else {
					comparison = String(aVal).localeCompare(String(bVal))
				}

				return sortDirection === 'desc' ? -comparison : comparison
			})
		}

		// Calculate pagination
		const totalCount = filteredData.length
		const offset = query.offset || 0
		const limit = query.limit || 100
		const rows = filteredData.slice(offset, offset + limit)

		return {
			rows: rows as unknown as DataRow[],
			totalCount,
			offset,
			limit,
			hasMore: offset + limit < totalCount,
			executionTime: 0,
			fromCache: true
		}
	}

	async count(_filters?: FilterGroup): Promise<number> {
		return this.cachedData.length
	}

	async dispose(): Promise<void> {
		this.cachedData = []
		this.isInitializedFlag = false
	}
}
